import React from "react";
import {
  Box,
  Badge,
  Avatar,
  Popover,
  IconButton,
  MenuList,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import { Bell, Check, Sidebar } from "@phosphor-icons/react";
import AvatarInitials from "./AvatarInitials";
import LogoIcon from "../../../assets/an_purple_white.png";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import { useNotifications } from "hooks/useNotifications";
import NotificationList from "components/NotificationCenter/NotificationList";
import NotificationEmptyState from "components/NotificationCenter/NotificationEmptyState";
import NotificationConfirmDeactivationDialog from "components/NotificationCenter/NotificationConfirmDeactivationDialog";
import LoadingButton from "@mui/lab/LoadingButton";
import { Stack } from "@mui/material";
import { CheckSquareOffset } from "@phosphor-icons/react";
import * as selectors from "../../../selectors";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";

export const Tools = ({
  userAvatarSrc,
  menuItems,
  userInitials,
  handleDrawerToggle,
}) => {
  const anchorEl = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [openSubMenu, setOpenSubMenu] = React.useState(false);
  const [anchorElSubMenu, setAnchorElSubMenu] = React.useState(false);
  const [subMenuItems, setSubMenuItems] = React.useState([]);
  const [openNotifications, setOpenNotifications] = React.useState(false);
  const theme = useTheme();

  // Get userId from redux (like desktop)
  const userId = useSelector(selectors.getUserId);
  // Use notifications hook
  const Notifications = useNotifications(userId);
  const {
    notifications,
    unreadCount,
    hasMore,
    loadingMore,
    deactivationDialogOpen,
    deactivationDialogData,
    handlers: {
      handleMarkAll,
      handleClick: handleNotificationClick,
      handleLoadMore,
      handleClose: handleNotificationClose,
      handleCloseDeactivationDialog,
    },
  } = Notifications;

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClickSubMenu = () => {
    setOpenSubMenu(!openSubMenu);
  };

  const handleMenuItemClicked = (clickHandler) => {
    setOpen(false);
    clickHandler();
  };

  const handleEventMenuItemClicked = (event, items, clickHandler) => {
    setAnchorElSubMenu(event.currentTarget);
    setSubMenuItems(items);
    setOpenSubMenu(true);
    clickHandler();
  };

  // New: open notification dialog on bell click
  const onBellClick = () => {
    setOpenNotifications(true);
  };
  const onNotificationDialogClose = () => {
    setOpenNotifications(false);
    handleNotificationClose();
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      width={"100%"}
      justifyContent={"space-between"}
      alignItems={"center"}
    >
      <IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerToggle}>
        <Sidebar size={24} color={"#D2B3EA"} />
      </IconButton>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={LogoIcon} style={{ height: 29, width: "auto" }} />
      </div>
      <Box display={"flex"} flexDirection={"row"} alignItems={"center"} justifyContent={"flex-end"}>
        {/* Notification Bell with Dialog */}
        {userId && (
          <>
            <IconButton sx={{ marginRight: 2 }} onClick={onBellClick}>
              <Badge badgeContent={unreadCount} color="warning" variant="dot">
                <Bell color="#D2B3EA" />
              </Badge>
            </IconButton>
            <SwipeableDrawer
              anchor="bottom"
              open={openNotifications}
              onClose={onNotificationDialogClose}
              onOpen={() => setOpenNotifications(true)}
              PaperProps={{
                style: {
                  borderRadius: "16px 16px 0 0",
                  minHeight: '90vh',
                  height: '93vh',
                  maxHeight: '95vh',
                  // Ensure the drawer takes 93% of the viewport height
                },
              }}
              // Add blur to the backdrop
              ModalProps={{
                BackdropProps: {
                  style: {
                    backdropFilter: 'blur(2px)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              {/* Notification Header */}
              <Box sx={{ pb: "4px", px: "4px" }}>
                {/* Swipe indicator */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    pt: 6,
                    pb: 6,
                  }}
                >
                  <Box
                    sx={{
                      width: '30px',
                      height: '6px',
                      backgroundColor: '#E0E0E0',
                      borderRadius: '12px',
                    }}
                  />
                </Box>
                <Box display="flex" alignItems="center" py="8px" px="16px">
                  <Typography variant="h3" px="8px" py="5px">
                    Notifications
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: "auto" }}>
                    {notifications.length > 0 && notifications.some(n => !n.is_read) ? (
                      <IconButton size="small" onClick={handleMarkAll}>
                        <CheckSquareOffset color={theme.palette.text.secondary} size={20} />
                      </IconButton>
                    ) : null}
                    {notifications.length > 0 && notifications.every(n => n.is_read) ? (
                      <Typography
                        variant="body2"
                        fontStyle="italic"
                        color="text.secondary"
                        sx={{ textAlign: 'right', width: '100%' }}
                      >
                        <Box component="span" fontStyle="normal">🎉</Box> No unread notifications
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    overflowY: "auto",
                    maxHeight: "60vh",
                    px: "4px",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#ccc",
                      borderRadius: "4px",
                    },
                  }}
                >
                  {notifications.length > 0 ? (
                    <>
                      <NotificationList notifications={notifications} onItemClick={handleNotificationClick} />
                      {hasMore && (
                        <Box display="flex" justifyContent="center" p={1} mb="8px">
                          <LoadingButton
                            onClick={handleLoadMore}
                            loading={loadingMore}
                            size="medium"
                            variant="text"
                          >
                            Read Older Notifications
                          </LoadingButton>
                        </Box>
                      )}
                    </>
                  ) : (
                    <NotificationEmptyState />
                  )}
                </Box>
              </Box>
            </SwipeableDrawer>
          </>
        )}

        <IconButton onClick={handleClick} ref={anchorEl}>
          {userAvatarSrc || !userInitials ? (
            <Avatar src={userAvatarSrc} sx={{ width: 30, height: 30 }} />
          ) : (
            <AvatarInitials userInitials={userInitials} />
          )}
        </IconButton>
        <Popover
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          open={open}
          anchorEl={anchorEl.current}
          onClose={handleClick}
          PaperProps={{
            style: { width: "150px", minWidth: "fit-content" },
          }}
        >
          <MenuList>
            {menuItems.map(
              (
                {
                  value,
                  label,
                  disabled = undefined,
                  icon = "",
                  divider = false,
                  typography = false,
                  clickHandler,
                  endIcon = "",
                  subMenuItems = null,
                },
                i
              ) => (
                <MenuItem
                  key={i}
                  value={value}
                  onClick={(event) =>
                    !subMenuItems
                      ? handleMenuItemClicked(clickHandler)
                      : handleEventMenuItemClicked(event, subMenuItems, clickHandler)
                  }
                  disabled={disabled}
                  divider={divider}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {icon !== "" ? (
                        <ListItemIcon>
                          {React.createElement(icon, {
                            weight: "duotone",
                            color: "text.secondary",
                          })}
                        </ListItemIcon>
                      ) : null}
                      {typography ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >{`${label}`}</Typography>
                      ) : (
                        label
                      )}
                    </Box>
                    {endIcon !== "" ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {React.createElement(endIcon, {
                          color: "text.secondary",
                        })}
                      </Box>
                    ) : null}
                  </Box>
                </MenuItem>
              )
            )}
          </MenuList>
        </Popover>
        <Popover
          anchorEl={anchorElSubMenu}
          open={openSubMenu || false}
          onClose={handleClickSubMenu}
          anchorOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
          PaperProps={{
            style: { minWidth: "264px" },
          }}
          style={{
            paddingTop: "2px",
            padding: "2px",
          }}
        >
          <MenuList>
            {subMenuItems &&
              subMenuItems.map(
                (
                  {
                    label,
                    avatar = false,
                    avatarInitials = undefined,
                    clickHandler,
                    checked,
                  },
                  i
                ) => (

                  <MenuItem
                    onClick={() => {
                      clickHandler();
                    }}
                    onClose={handleClickSubMenu}
                    key={i}
                  >
                    {avatar && (
                      <Avatar sx={{ mr: theme.spacing(3) }} size="24px">
                        {avatarInitials}
                      </Avatar>
                    )}
                    <Typography flexGrow={1} variant="body2">
                      {label}
                    </Typography>
                    {checked && (
                      <Check size={24} color={theme.palette.primary.main} weight="regular" />
                    )}
                  </MenuItem>
                )
              )}
          </MenuList>
        </Popover>

        {/* Deactivation Confirmation Dialog */}
        {deactivationDialogData && (
          <NotificationConfirmDeactivationDialog
            open={deactivationDialogOpen}
            onClose={handleCloseDeactivationDialog}
            clientId={deactivationDialogData.clientId}
            clientName={deactivationDialogData.clientName}
          />
        )}
      </Box>
    </Box>
  );
};
